/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getBarGapsInfo } from './barSeriesUtils';
import { getRectCoord } from './barItemUtils';
import { BarSeriesData } from '../barChart.types';
import { ChartItemData, Scale, Group } from '../../utils/UNSAFE_visTypes/chart';

/**
 * Returns the base and end coordinate of the bar.
 * @param value The value of the bar item.
 * @param yScale The yscale.
 * @param isStacked Whether the bar chart is stacked or not.
 * @param isLog Whether the y axis is in log scale.
 * @param itemsGaps The gap between data item.
 * @param negCummulativeValue The negative cummulative value of the group.
 * @param posCummulativeValue The positive cummulative value of the group.
 * @returns
 */
export function getBaseAndEndCoord(
  value: number,
  yScale: Scale,
  isStacked: boolean,
  isLog: boolean,
  itemsGaps: number,
  negCummulativeValue: number,
  posCummulativeValue: number
) {
  let baselineCoord;
  let endCoord;
  if (isStacked) {
    baselineCoord = yScale.transform(value < 0 ? negCummulativeValue : posCummulativeValue);
    negCummulativeValue += value < 0 ? value : 0;
    posCummulativeValue += value > 0 ? value : 0;
    endCoord = yScale.transform(value < 0 ? negCummulativeValue : posCummulativeValue);

    baselineCoord += endCoord > baselineCoord ? itemsGaps : -itemsGaps;
  } else {
    baselineCoord = isLog ? yScale.range()[0] : yScale.transform(0);
    endCoord = yScale.transform(value);
  }
  return { baselineCoord, endCoord };
}

export function getBarPosition<K extends string | number, D extends ChartItemData<K>>(
  series: BarSeriesData<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  xScale: Scale,
  yScale: Scale,
  hiddenIds: Set<number | string> | undefined,
  dataItemGaps: number,
  groupWidth: number,
  barGapRatio: number,
  isLog: boolean,
  isStacked: boolean,
  isRtl: boolean,
  isHoriz: boolean,
  timeAxisType?: 'mixedFrequency' | 'enabled' | 'skipGaps'
) {
  return (seriesIndex: number, groupIndex: number) => {
    const numOfSeries = series.length;
    let posCumulativeValue = 0;
    let negCumulativeValue = 0;
    for (let index = 0; index < numOfSeries; index += 1) {
      const item = getDataItem(index, groupIndex);
      let xVal = groupIndex;
      if (timeAxisType) {
        const val =
          timeAxisType === 'mixedFrequency'
            ? getDataItem(seriesIndex, groupIndex)?.x
            : groups[groupIndex].id;
        xVal = new Date(val!).getTime();
      }
      if (!item || hiddenIds?.has(item.id) || (isLog && item.value <= 0)) {
        continue;
      }
      const groupCenter = xScale.transform(xVal);
      const { barWidth, offset, itemsGaps } = getBarGapsInfo(
        index,
        groupWidth,
        barGapRatio,
        numOfSeries,
        dataItemGaps,
        isStacked,
        isRtl
      );
      const x1 = groupCenter + offset;
      const x2 = x1 + barWidth;

      const { baselineCoord, endCoord } = getBaseAndEndCoord(
        item.value,
        yScale,
        isStacked,
        isLog,
        itemsGaps,
        negCumulativeValue,
        posCumulativeValue
      );
      negCumulativeValue += item.value < 0 ? item.value : 0;
      posCumulativeValue += item.value > 0 ? item.value : 0;

      if (seriesIndex === index) {
        return getRectCoord(x1, x2, endCoord, baselineCoord, isHoriz);
      }
    }
    return;
  };
}

/**
 * Returns the center coordinate of the bar item.
 * @param value
 * @param yScale
 * @param isStacked
 * @param isLog
 * @param dataItemGaps
 * @param negCummValue
 * @param posCummValue
 * @returns
 */
export function getBarCenterCoordinate(
  value: number,
  yScale: Scale,
  isStacked: boolean,
  isLog: boolean,
  dataItemGaps: number,
  negCummValue: number,
  posCummValue: number
) {
  const { baselineCoord, endCoord } = getBaseAndEndCoord(
    value,
    yScale,
    isStacked,
    isLog,
    dataItemGaps,
    negCummValue,
    posCummValue
  );
  return (baselineCoord + endCoord) / 2;
}

export function getSeriesOffsetAndWidth(
  groupWidth: number,
  barGapRatio: number,
  numOfSeries: number,
  dataItemGaps: number,
  isStacked: boolean,
  isRtl: boolean
) {
  return (seriesIndex: number) => {
    const { offset, barWidth } = getBarGapsInfo(
      seriesIndex,
      groupWidth,
      barGapRatio,
      numOfSeries,
      dataItemGaps,
      isStacked,
      isRtl
    );
    return { offset, dataWidth: barWidth };
  };
}
